import React, { useState, useEffect, useRef, useCallback } from "react";
import { useTranslation } from "react-i18next";
import Layout from "../../components/layout/Layout";
import { SectionTitle, Modal, LoadingSpinner } from "@multiagentbook/ui";
import {
  BookOpen,
  Download,
  ExternalLink,
  AlertCircle,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import { CHECKOUT_URL } from "../../data/siteLinks";

// Languages with generated preview images (see scripts/generate-preview-images.sh)
const AVAILABLE_LANGUAGES = [
  { code: "en", name: "English" },
  { code: "de", name: "German" },
  { code: "es", name: "Spanish" },
  { code: "fr", name: "French" },
  { code: "zh", name: "Chinese" },
  { code: "ja", name: "Japanese" },
  { code: "ru", name: "Russian" },
] as const;

type PreviewLanguage = (typeof AVAILABLE_LANGUAGES)[number]["code"];

// Total chapters in the full book (preview covers the Preface and Chapter 1)
const TOTAL_CHAPTERS = 15;

interface PageManifest {
  language: string;
  pages: number;
  startPage?: number;
  full: { width: number; height: number; pattern: string };
  thumb: { width: number; height: number; pattern: string };
}

const pad = (n: number) => String(n).padStart(2, "0");

// A single book page with its own blur-up loading state (so spreads load independently)
const PageImage: React.FC<{
  src: string;
  aspectRatio: string;
  alt: string;
  onClick?: () => void;
  overlay?: React.ReactNode;
}> = ({ src, aspectRatio, alt, onClick, overlay }) => {
  const [loaded, setLoaded] = useState(false);
  return (
    <div className="relative flex-1 min-w-0" style={{ aspectRatio }}>
      {!loaded && (
        <div className="absolute inset-0 rounded-sm shadow-xl animate-pulse bg-gradient-to-br from-secondary/10 to-secondary/20" />
      )}
      <img
        src={src}
        alt={alt}
        onLoad={() => setLoaded(true)}
        className={`absolute inset-0 w-full h-full object-contain rounded-sm bg-white shadow-xl ring-1 ring-black/5 transition-opacity duration-300 ${
          loaded ? "opacity-100" : "opacity-0"
        }`}
      />
      {/* transparent layer: click the page to expand */}
      <button
        type="button"
        onClick={onClick}
        aria-label={alt}
        className="absolute inset-0 rounded-sm cursor-zoom-in transition-shadow hover:ring-2 hover:ring-accent/40"
      />
      {overlay}
    </div>
  );
};

// Optional deep-link: /preview?page=12
const pageFromUrl = (): number | null => {
  if (typeof window === "undefined") return null;
  const p = parseInt(
    new URLSearchParams(window.location.search).get("page") || "",
    10
  );
  return Number.isFinite(p) && p > 0 ? p : null;
};

const PreviewPage: React.FC = () => {
  const { t, i18n } = useTranslation("preview");
  const [language, setLanguage] = useState<PreviewLanguage>("en");
  const [manifest, setManifest] = useState<PageManifest | null>(null);
  const [loadError, setLoadError] = useState(false);
  const [page, setPage] = useState(1);
  const [spread, setSpread] = useState(false);
  const [zoomOpen, setZoomOpen] = useState(false);
  const [zoomPage, setZoomPage] = useState(1);

  const filmstripRef = useRef<HTMLDivElement>(null);

  // Two-page spread on wide screens (efficient use of horizontal space)
  useEffect(() => {
    const mq = window.matchMedia("(min-width: 1024px)");
    const update = () => setSpread(mq.matches);
    update();
    mq.addEventListener("change", update);
    return () => mq.removeEventListener("change", update);
  }, []);

  const basePath = `/previews/pages/${language}`;
  const pdfUrl = `/previews/preview-${language}.pdf`;
  const fullSrc = (n: number) => `${basePath}/full-${pad(n)}.webp`;
  const thumbSrc = (n: number) => `${basePath}/thumb-${pad(n)}.webp`;
  const isTranslation = language !== "en";

  // Match the user's language if a preview exists for it
  useEffect(() => {
    const userLang = i18n.language.split("-")[0];
    if (AVAILABLE_LANGUAGES.some((l) => l.code === userLang)) {
      setLanguage(userLang as PreviewLanguage);
    }
  }, [i18n.language]);

  // Load the manifest whenever the language changes
  useEffect(() => {
    let cancelled = false;
    setManifest(null);
    setLoadError(false);
    fetch(`${basePath}/manifest.json`)
      .then((r) => {
        if (!r.ok) throw new Error("manifest not found");
        return r.json();
      })
      .then((data: PageManifest) => {
        if (cancelled) return;
        const desired = pageFromUrl() ?? data.startPage ?? 1;
        setPage(Math.min(Math.max(1, desired), data.pages));
        setManifest(data);
      })
      .catch(() => {
        if (!cancelled) setLoadError(true);
      });
    return () => {
      cancelled = true;
    };
  }, [basePath]);

  const goTo = useCallback(
    (n: number) => {
      if (!manifest) return;
      setPage(Math.min(Math.max(1, n), manifest.pages));
    },
    [manifest]
  );

  // The pages currently on screen: a two-page spread on wide screens, else one.
  // Spreads are even-left / odd-right (e.g. 2|3, 4|5) like a printed book.
  const spreadLeft = page % 2 === 0 ? page : Math.max(1, page - 1);
  const pagesToShow = manifest
    ? spread
      ? [spreadLeft, spreadLeft + 1].filter((n) => n >= 1 && n <= manifest.pages)
      : [page]
    : [];
  const step = spread ? 2 : 1;
  const navBase = spread ? spreadLeft : page;
  const canPrev = navBase - step >= 1;
  const canNext = manifest ? navBase + step <= manifest.pages : false;

  // Preload the next spread/page for snappy navigation
  useEffect(() => {
    if (!manifest) return;
    [navBase + step, navBase + step + 1].forEach((n) => {
      if (n >= 1 && n <= manifest.pages) {
        const img = new Image();
        img.src = fullSrc(n);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, language, manifest, spread]);

  // Keyboard navigation
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (zoomOpen) return;
      if (e.key === "ArrowRight") goTo(navBase + step);
      if (e.key === "ArrowLeft") goTo(navBase - step);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [navBase, step, zoomOpen, goTo]);

  // Arrow-key navigation inside the expanded (zoom) view
  useEffect(() => {
    if (!zoomOpen || !manifest) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "ArrowRight")
        setZoomPage((p) => Math.min(manifest.pages, p + 1));
      if (e.key === "ArrowLeft") setZoomPage((p) => Math.max(1, p - 1));
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [zoomOpen, manifest]);

  const openZoom = (n: number) => {
    setZoomPage(n);
    setZoomOpen(true);
  };

  // Keep the active thumbnail in view
  useEffect(() => {
    const el = filmstripRef.current?.querySelector(`[data-page="${page}"]`);
    el?.scrollIntoView({ block: "nearest", inline: "nearest" });
  }, [page]);

  const siteMetadata = {
    description: t("subtitle"),
    title: t("title"),
    author: "Victor Dibia",
    image: "/images/og-card.png",
    keywords:
      "multi-agent systems book preview, AI agents book sample, free AI book preview",
    twitterUsername: "@designingwithml",
  };

  const pageCount = manifest?.pages ?? 45;

  return (
    <Layout meta={siteMetadata} title={t("title")} link="/preview">
      <main className="py-8">
        {/* Header */}
        <div className="mb-4">
          <SectionTitle title={t("title")} />
          <p className="text-lg text-secondary mt-2 max-w-3xl">{t("subtitle")}</p>
          <p className="text-sm text-secondary/80 mt-2">
            {t("scope", {
              pages: pageCount,
              chapters: TOTAL_CHAPTERS,
              languages: AVAILABLE_LANGUAGES.length,
            })}
          </p>
        </div>

        {/* Controls: language + download */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4">
          <div>
            <span className="text-sm font-medium text-secondary mr-3">
              {t("languageSelector.label")}
            </span>
            <div className="inline-flex flex-wrap gap-2 mt-2 sm:mt-0">
              {AVAILABLE_LANGUAGES.map((lang) => (
                <button
                  key={lang.code}
                  onClick={() => setLanguage(lang.code)}
                  className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                    language === lang.code
                      ? "bg-accent text-white"
                      : "bg-secondary/10 text-primary hover:bg-secondary/20 border border-secondary/20"
                  }`}
                >
                  {t(`languages.${lang.code}`)}
                </button>
              ))}
            </div>
          </div>
          <a
            href={CHECKOUT_URL}
            className="inline-flex items-center gap-2 px-5 py-2 bg-accent text-white rounded-lg hover:bg-accent/90 hover:text-white transition-colors font-medium text-sm self-start"
          >
            <BookOpen className="w-4 h-4" />
            {t("cta.buyDigital")}
          </a>
        </div>

        {/* Translation disclaimer */}
        {isTranslation && (
          <div className="mb-4 p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-amber-800 dark:text-amber-200">
              {t("languageSelector.translationDisclaimer")}
            </p>
          </div>
        )}

        {/* Reader */}
        {loadError ? (
          <div className="rounded-xl border border-secondary/20 p-8 text-center">
            <p className="text-secondary mb-4">{t("reader.error")}</p>
            <a
              href={pdfUrl}
              download
              className="inline-flex items-center gap-2 px-4 py-2 bg-accent text-white rounded-lg hover:bg-accent/90 hover:text-white transition-colors font-medium text-sm"
            >
              <Download className="w-4 h-4" />
              {t("cta.downloadPdf")}
            </a>
          </div>
        ) : !manifest ? (
          <div className="flex items-center justify-center h-[60vh] rounded-xl border border-secondary/20">
            <LoadingSpinner />
          </div>
        ) : (
          <div className="rounded-xl bg-gradient-to-br from-accent/[0.05] via-secondary/[0.03] to-accent/[0.08] px-1 py-6 md:px-2 md:py-8">
            {/* Page stage (single page, or a two-page spread on wide screens) */}
            <div className="flex items-stretch justify-center gap-4 lg:gap-8">
              {pagesToShow.map((n) => (
                <PageImage
                  key={`${language}-${n}`}
                  src={fullSrc(n)}
                  aspectRatio={`${manifest.full.width} / ${manifest.full.height}`}
                  alt={`Preview page ${n}`}
                  onClick={() => openZoom(n)}
                  overlay={
                    n === manifest.pages ? (
                      <div className="absolute inset-0 rounded-sm flex items-end justify-center p-4 bg-gradient-to-t from-black/20 to-transparent">
                        <div className="w-full max-w-[230px] rounded-lg bg-white/95 backdrop-blur shadow-lg ring-1 ring-black/5 px-4 py-3 text-center">
                          <p className="text-xs font-semibold text-gray-900">
                            {t("endCard.title")}
                          </p>
                          <div className="mt-2.5 flex flex-col gap-1.5">
                            <a
                              href={CHECKOUT_URL}
                              className="inline-flex items-center justify-center gap-1.5 px-3 py-1.5 bg-accent text-white rounded-md hover:bg-accent/90 hover:text-white transition-colors font-medium text-xs"
                            >
                              <BookOpen className="w-3.5 h-3.5" />
                              {t("cta.buyDigital")}
                            </a>
                            <a
                              href="https://www.amazon.com/dp/B0G2BCQQJY"
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center justify-center gap-1.5 px-3 py-1.5 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors font-medium text-xs"
                            >
                              <ExternalLink className="w-3.5 h-3.5" />
                              {t("cta.buyPrint")}
                            </a>
                          </div>
                        </div>
                      </div>
                    ) : undefined
                  }
                />
              ))}
            </div>

            {/* Control pill */}
            <div className="mt-5 flex justify-center">
              <div className="inline-flex items-center gap-1 rounded-full border border-secondary/15 bg-white/80 dark:bg-slate-800/80 backdrop-blur px-1.5 py-1 shadow-sm">
                <button
                  onClick={() => goTo(navBase - step)}
                  disabled={!canPrev}
                  aria-label={t("reader.prev")}
                  className="p-1.5 rounded-full text-primary hover:bg-secondary/15 disabled:opacity-30 disabled:hover:bg-transparent transition-colors"
                >
                  <ChevronLeft className="w-5 h-5" />
                </button>
                <span className="px-2 text-sm text-secondary tabular-nums select-none min-w-[88px] text-center">
                  {t("reader.page", {
                    current:
                      pagesToShow.length > 1
                        ? `${pagesToShow[0]}–${pagesToShow[pagesToShow.length - 1]}`
                        : pagesToShow[0] ?? page,
                    total: manifest.pages,
                  })}
                </span>
                <button
                  onClick={() => goTo(navBase + step)}
                  disabled={!canNext}
                  aria-label={t("reader.next")}
                  className="p-1.5 rounded-full text-primary hover:bg-secondary/15 disabled:opacity-30 disabled:hover:bg-transparent transition-colors"
                >
                  <ChevronRight className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* Horizontal filmstrip */}
            <div
              ref={filmstripRef}
              className="mt-5 flex gap-2 overflow-x-auto px-1 pb-2 [scrollbar-width:thin] [scrollbar-color:rgba(120,120,120,0.3)_transparent] [&::-webkit-scrollbar]:h-1.5 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-secondary/30 hover:[&::-webkit-scrollbar-thumb]:bg-secondary/50"
            >
              {Array.from({ length: manifest.pages }, (_, i) => i + 1).map((n) => (
                <button
                  key={n}
                  data-page={n}
                  onClick={() => goTo(n)}
                  aria-label={`Page ${n}`}
                  className={`relative flex-shrink-0 rounded overflow-hidden transition-all duration-150 ${
                    pagesToShow.includes(n)
                      ? "ring-2 ring-accent ring-offset-2 ring-offset-transparent"
                      : "opacity-50 hover:opacity-100 hover:-translate-y-0.5"
                  }`}
                >
                  <img
                    src={thumbSrc(n)}
                    loading="lazy"
                    decoding="async"
                    width={manifest.thumb.width}
                    height={manifest.thumb.height}
                    alt={`Preview page ${n}`}
                    className="block w-[42px] h-auto bg-white"
                  />
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Boundary CTA */}
        <div className="mt-12 bg-gradient-to-r from-accent/10 to-accent/5 rounded-xl p-6">
          <h2 className="text-2xl font-bold text-primary mb-2">
            {t("cta.getFullBook")}
          </h2>
          <p className="text-secondary mb-6 max-w-xl">{t("cta.getFullBookDesc")}</p>
          <div className="flex flex-wrap gap-4">
            <a
              href={CHECKOUT_URL}
              className="inline-flex items-center gap-2 px-6 py-3 bg-accent text-white rounded-lg hover:bg-accent/90 hover:text-white transition-colors font-medium"
            >
              <BookOpen className="w-5 h-5" />
              {t("cta.buyDigital")}
            </a>
            <a
              href="https://www.amazon.com/dp/B0G2BCQQJY"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-6 py-3 border border-secondary/30 text-primary rounded-lg hover:bg-secondary/10 transition-colors font-medium"
            >
              <ExternalLink className="w-5 h-5" />
              {t("cta.buyPrint")}
            </a>
          </div>
        </div>
      </main>

      {/* Full-size zoom */}
      <Modal
        isOpen={zoomOpen}
        onClose={() => setZoomOpen(false)}
        size="full"
        title={t("reader.page", {
          current: zoomPage,
          total: manifest?.pages ?? pageCount,
        })}
      >
        <div className="relative h-[85vh] overflow-auto bg-secondary/10 p-4 flex justify-center">
          <img
            src={fullSrc(zoomPage)}
            alt={`Preview page ${zoomPage}`}
            className="h-auto w-auto self-start"
            style={{ maxWidth: "min(100%, 900px)" }}
          />
          <button
            onClick={() => setZoomPage((p) => Math.max(1, p - 1))}
            disabled={zoomPage <= 1}
            aria-label={t("reader.prev")}
            className="fixed left-4 top-1/2 -translate-y-1/2 p-3 rounded-full bg-black/50 text-white hover:bg-black/70 disabled:opacity-30 transition-colors"
          >
            <ChevronLeft className="w-6 h-6" />
          </button>
          <button
            onClick={() =>
              setZoomPage((p) => Math.min(manifest?.pages ?? p, p + 1))
            }
            disabled={zoomPage >= (manifest?.pages ?? 1)}
            aria-label={t("reader.next")}
            className="fixed right-4 top-1/2 -translate-y-1/2 p-3 rounded-full bg-black/50 text-white hover:bg-black/70 disabled:opacity-30 transition-colors"
          >
            <ChevronRight className="w-6 h-6" />
          </button>
        </div>
      </Modal>
    </Layout>
  );
};

export default PreviewPage;
